import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

interface OnlineAdmin {
  user_id: string;
  full_name: string;
  system_role: string;
  last_activity: string;
  ip_address: string | null;
}

export function useAdminPresence() {
  const { user, profile } = useAuth();
  const [onlineAdmins, setOnlineAdmins] = useState<OnlineAdmin[]>([]);
  const [loading, setLoading] = useState(true);
  const [sessionId] = useState(() => crypto.randomUUID());

  const isAdmin = profile?.system_role === 'admin' || profile?.system_role === 'super_admin';
  const isStaff = isAdmin || profile?.system_role === 'vendor';

  // Mettre à jour l'activité (heartbeat)
  const updateActivity = useCallback(async () => {
    if (!user || !isStaff) return;

    try {
      await supabase.rpc('update_admin_activity', {
        p_session_id: sessionId,
        p_ip_address: null,
        p_user_agent: navigator.userAgent
      });
    } catch (error) {
      console.error('Error updating admin activity:', error);
    }
  }, [user, isStaff, sessionId]);

  // Récupérer la liste des admins connectés
  const fetchOnlineAdmins = useCallback(async () => {
    try {
      const { data, error } = await supabase.rpc('get_online_admins');
      
      if (error) {
        console.error('Error fetching online admins:', error);
        return;
      }

      setOnlineAdmins(data || []);
    } catch (error) {
      console.error('Error in fetchOnlineAdmins:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Terminer la session
  const endSession = useCallback(async () => {
    if (!user || !isStaff) return;

    try {
      await supabase.rpc('end_admin_session', {
        p_session_id: sessionId
      });
    } catch (error) {
      console.error('Error ending admin session:', error);
    }
  }, [user, isStaff, sessionId]);

  // Heartbeat toutes les 30 secondes
  useEffect(() => {
    if (!isStaff) return;

    // Premier heartbeat immédiat
    updateActivity();
    fetchOnlineAdmins();

    // Heartbeat régulier
    const heartbeatInterval = setInterval(() => {
      updateActivity();
    }, 30000); // 30 secondes

    // Rafraîchir la liste des admins connectés
    const refreshInterval = setInterval(() => {
      fetchOnlineAdmins();
    }, 15000); // 15 secondes

    // Terminer la session à la fermeture
    const handleBeforeUnload = () => {
      // Utiliser sendBeacon pour garantir l'envoi
      const data = JSON.stringify({
        action: 'end_session',
        session_id: sessionId
      });
      navigator.sendBeacon('/api/admin-presence', data);
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      clearInterval(heartbeatInterval);
      clearInterval(refreshInterval);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      endSession();
    };
  }, [isStaff, updateActivity, fetchOnlineAdmins, endSession, sessionId]);

  // Écouter les changements en temps réel
  useEffect(() => {
    if (!isAdmin) return;

    const channel = supabase
      .channel('admin_presence')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'admin_sessions'
        },
        () => {
          fetchOnlineAdmins();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [isAdmin, fetchOnlineAdmins]);

  return {
    onlineAdmins,
    loading,
    isOnline: (userId: string) => onlineAdmins.some(a => a.user_id === userId),
    refreshOnlineAdmins: fetchOnlineAdmins
  };
}